import {OwnerId, ReviewAction, ReviewerRoleId} from "platformed-client/types";
import {q, useQueryData} from "../../../state";
import {usePromiseState} from "../../../hooks/promiseState";
import {ReviewItem} from ".";
import SubmitReviewButton from "./SubmitReviewButton";
import {useCreateReviews} from "./useCreateReviews";
import {Button} from "../../../components/ui/button";
import T from "platformed-components/i18n/T";

const RequestNewReviewButton = ({
  reviewItem,
  ownedBy,
  reviewerRoleId,
}: {
  reviewItem: ReviewItem;
  ownedBy: OwnerId;
  reviewerRoleId?: ReviewerRoleId;
}) => {
  const createReviews = useCreateReviews(reviewItem);
  const [requestingNewReview, requestNewReview] = usePromiseState(
    () => createReviews([{review_status: "Pending", reviewer_id: ownedBy, reviewer_role_id: reviewerRoleId}]),
    [createReviews, ownedBy, reviewerRoleId],
  );

  return (
    <Button size="2xs" variant="outline" onClick={requestNewReview} disabled={requestingNewReview.inProgress}>
      <T keyName="c.Request_new_review" />
    </Button>
  );
};

/**
 * The per-review action control, shared by the review list and the plan drawer:
 * "Edit your review" for the reviewer who already actioned it, "Submit review"
 * for an eligible pending reviewer, and (optionally) "Request new review" to
 * re-request a completed review. Renders nothing when the current user can't act.
 */
const ReviewActionButton = ({
  review,
  reviewItem,
  showRequestNew = false,
}: {
  review: ReviewAction;
  reviewItem: ReviewItem;
  /** Show a "Request new review" button for completed reviews the user can't edit. */
  showRequestNew?: boolean;
}) => {
  const {user} = useQueryData(q.whoAmI());
  const {associated_owners} = useQueryData(q.whoAmIOnAccount());
  const status = review.review_status;

  if (review.actioned.by?.user_id === user.user_id && status !== "Pending") {
    return (
      <SubmitReviewButton reviewItem={reviewItem} review={review} size="2xs" colorPalette="blue">
        <T keyName="c.Edit_your_review" />
      </SubmitReviewButton>
    );
  }
  if (associated_owners.map(o => o.owner_id).includes(review.reviewer.owner_id) && status === "Pending") {
    return (
      <SubmitReviewButton reviewItem={reviewItem} review={review} size="2xs" colorPalette="blue">
        {review.reviewer.owner_payload.type === "Team" ? (
          <T
            keyName="c.Submit_team_review[parameterized]"
            params={{team: review.reviewer.owner_payload.content.name}}
          />
        ) : (
          <T keyName="c.Submit_review" />
        )}
      </SubmitReviewButton>
    );
  }
  if (showRequestNew && status !== "Pending") {
    return (
      <RequestNewReviewButton
        reviewItem={reviewItem}
        ownedBy={review.reviewer.owner_id}
        reviewerRoleId={review.reviewer_role_id ?? undefined}
      />
    );
  }
  return null;
};

export default ReviewActionButton;
